import React, { useEffect, useState } from "react";
import { apiClient } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Wallet, ArrowDown, HelpCircle } from "lucide-react";

function chf(v, currency = "CHF") {
  const sign = v < 0 ? "−" : "+";
  const abs = Math.abs(v).toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${sign} ${currency} ${abs}`;
}

function chfPlain(v, currency = "CHF") {
  return `${currency} ${v.toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export const CashflowBridgeCard = ({ companyId }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!companyId) return;
    apiClient
      .get(`/companies/${companyId}/dashboard/cashflow`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [companyId]);

  if (!data) return null;

  const rows = [
    ...(data.items || []),
    ...(Math.abs(data.unexplained || 0) >= 0.05
      ? [{ label: "Übrige/nicht zugeordnet", amount: data.unexplained }]
      : []),
  ];

  return (
    <Card className="p-5" data-testid="dashboard-cashflow-card">
      <div className="flex items-center gap-2 mb-1">
        <div className="h-8 w-8 rounded-lg bg-sky-600 text-white flex items-center justify-center">
          <Wallet className="h-4 w-4" />
        </div>
        <div>
          <h3 className="font-semibold text-slate-900 text-sm">Vom Gewinn zum Kontostand</h3>
          <p className="text-[11px] text-slate-500">
            Warum Ergebnis und Bank {data.year} nicht dasselbe sind
          </p>
        </div>
      </div>

      <div className="mt-3 space-y-1.5 text-sm">
        <div className="flex justify-between items-center py-1 border-b border-slate-100">
          <span className="font-medium text-slate-700">Ergebnis {data.year} (Erfolgsrechnung)</span>
          <span
            className={`font-semibold ${data.result < 0 ? "text-rose-600" : "text-emerald-600"}`}
            data-testid="cashflow-result"
          >
            {chf(data.result, data.currency)}
          </span>
        </div>

        {rows.map((it, i) => (
          <div key={i} className="flex justify-between items-center gap-3 py-0.5">
            <span className="text-slate-600 text-[13px]">{it.label}</span>
            <span
              className={`text-[13px] font-medium whitespace-nowrap ${
                it.amount < 0 ? "text-rose-600" : "text-emerald-600"
              }`}
            >
              {chf(it.amount, data.currency)}
            </span>
          </div>
        ))}

        <div className="flex justify-between items-center pt-2 border-t border-slate-200">
          <span className="flex items-center gap-1 font-medium text-slate-700">
            <ArrowDown className="h-3.5 w-3.5 text-slate-400" />
            Veränderung flüssige Mittel
          </span>
          <span
            className={`font-semibold ${data.delta_liquidity < 0 ? "text-rose-600" : "text-emerald-600"}`}
            data-testid="cashflow-delta"
          >
            {chf(data.delta_liquidity, data.currency)}
          </span>
        </div>
        <div className="flex justify-between items-center text-[13px] text-slate-500">
          <span>Anfangsbestand 01.01.{data.year}</span>
          <span>{chfPlain(data.opening_liquidity, data.currency)}</span>
        </div>
        <div className="flex justify-between items-center py-1.5 px-2 -mx-2 rounded-lg bg-sky-50">
          <span className="font-semibold text-slate-900">Flüssige Mittel heute</span>
          <span className="font-bold text-sky-700" data-testid="cashflow-closing">
            {chfPlain(data.closing_liquidity, data.currency)}
          </span>
        </div>
      </div>

      <p className="mt-2 text-[11px] text-slate-400 flex items-start gap-1">
        <HelpCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
        Darlehen, Anlagenkäufe und MWST bewegen Geld ohne Gewinn zu sein — oder umgekehrt.
      </p>
    </Card>
  );
};
